/**
 * Master Entity Definition
 *
 * Single table for all reference data, discriminated by `type`.
 * Type-specific fields stored in `metadata` JSON column.
 */

import type { CollectionEntityDefinition, RolePermission } from '@gzl10/nexus-sdk'
import {
  useTextField, useLocalizedField, useJsonField,
  isActiveField, orderField
} from '@gzl10/nexus-sdk/fields'

const masterCaslPermissions: Record<string, RolePermission> = {
  '*': { actions: ['read'] },
  ADMIN: { actions: ['manage'] }
}

export const mastersEntity: CollectionEntityDefinition = {
  type: 'collection',
  table: "masters",
  seedable: true,
  routePrefix: "/",
  label: { en: "Master", es: "Maestro" },
  labelPlural: { en: "Masters", es: "Maestros" },
  labelField: "label",
  timestamps: true,
  availableDisplayModes: ['board'],
  groupBy: "type",
  groupableFields: ['type'],
  //columnDragFields: ['is_active'],
  fields: {
    id: useTextField({
      label: { en: "ID", es: "ID" },
      required: true,
      size: 100,
      hidden: true,
      meta: { sortable: true },
    }),
    type: useTextField({
      label: { en: "Type", es: "Tipo" },
      required: true,
      size: 50,
      index: true,
      meta: { sortable: true, searchable: true },
    }),
    code: useTextField({
      label: { en: "Code", es: "Código" },
      required: true,
      size: 50,
      meta: { sortable: true, searchable: true },
    }),
    label: useLocalizedField({ label: { en: "Name", es: "Nombre" } }),
    order: { ...orderField, meta: { ...orderField.meta, showInDisplay: false } },
    is_active: isActiveField,
    metadata: useJsonField({
      label: { en: "Metadata", es: "Metadatos" },
      hint: {
        en: "Type-specific fields (symbol, flag, etc.)",
        es: "Campos específicos del tipo",
      },
      meta: { showInDisplay: false },
    }),
  },
  hooks: () => ({
    beforeCreate: async (data: Record<string, unknown>) => {
      if (data['type'] && data['code'] && !data['id']) {
        data['id'] = `${data['type']}:${data['code']}`
      }
      return data
    },
    beforeUpdate: async (_id: string, data: Partial<Record<string, unknown>>) => {
      if ('type' in data || 'code' in data) {
        throw new Error('Cannot update type or code of a master record. Delete and recreate instead.')
      }
      return data
    }
  }),
  defaultSort: { field: "order", order: "asc" },
  indexes: [{ columns: ["type", "code"], unique: true }],
  casl: { subject: "Master", permissions: masterCaslPermissions },
};
